// GA送信周りの処理

// データレイヤーを取得する共通関数
const getDataLayer = () => {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  (window as any).dataLayer = (window as any).dataLayer || [];
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const dataLayer = (window as any).dataLayer;
  return dataLayer;
};

// ※ユーザーIDのみuser_idとスネークケースなのは、GA上で自動で付与されるuserIdと被らないようにするため

/**
 * ecommerceイベント送信時の注意事項
 *
 * GA4では、ecommerceイベントを送信する前に前回のecommerceオブジェクトをクリアすることが推奨されています。
 * Next.jsはSPAとして動作するため、ページ遷移してもwindow.dataLayerが保持され、
 * 古いecommerceデータが残ったままになる可能性があります。
 *
 * 各ecommerceイベント関数では、以下のパターンでクリア処理を実施しています：
 * ```typescript
 * getDataLayer().push({ ecommerce: null });
 * getDataLayer().push({ event: 'xxx', ecommerce: { ... } });
 * ```
 *
 * 参考: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm&hl=ja
 */

/**
 * GA4推奨イベントの商品アイテムパラメーターの型
 * 詳しい説明は以下を参照
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?sjid=10436666439109070913-NC&hl=ja&client_type=gtag#purchase_item
 */
type PurchaseItem = {
  item_id: string;
  item_name: string;
  item_brand: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
  material_ids?: string;
};

// デザインツール - パーツ切り替え
export const sendSwitchDesignPartsEvent = (
  productId: number,
  productName: string,
  partId: number,
  partName: string,
  user_id: number,
) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'design_tool_switch_parts',
    productId,
    productName,
    partId,
    partName,
    user_id,
  });
};

// デザインツール - 素材をデザインから削除
export const sendRemoveMaterialToDesignEvent = (
  materialId: number,
  productId: number,
  productName: string,
  partId: number,
  partName: string,
  user_id: number,
) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'remove_material_from_design',
    materialId,
    productId,
    productName,
    partId,
    partName,
    user_id,
  });
};

// デザインツール - 素材を選択領域に追加
export const sendAddMaterialToPalletEvent = (materialId: number, materialName: string, user_id: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'add_material_to_pallet',
    materialId,
    materialName,
    user_id,
  });
};

// デザインツール - 素材を選択領域から削除
export const sendRemoveMaterialToPalletEvent = (materialId: number, materialName: string, user_id: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'remove_material_to_pallet',
    materialId,
    materialName,
    user_id,
  });
};

// デザインツール - プレビューを開く
export const sendOpenPreviewEvent = (user_id: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'open_preview',
    user_id,
  });
};

// デザインツール - プレビューから戻る
export const sendClosePreviewEvent = (user_id: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'close_preview',
    user_id,
  });
};

// 作品を選ぶ - TOPページからの遷移
export const sendWorkNavigationFromTopEvent = (workId: number, workName: string, user_id: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'work_navigation_from_top',
    workId,
    workName,
    user_id,
  });
};

// 作品一覧 - 表示形式切り替え
export const sendWorksViewModeSwitchEvent = (workViewMode: string, user_id: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'works_view_mode_switch',
    workViewMode,
    user_id,
  });
};

// 作品一覧 - ソート切り替え
export const sendWorksSortChangeEvent = (workSortType: string, user_id: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'works_sort_change',
    workSortType,
    user_id,
  });
};

// 素材を選ぶ - 素材クリック
export const sendMaterialClickEvent = (materialId: number, materialName: string, user_id: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'material_click',
    materialId,
    materialName,
    user_id,
  });
};

// 素材を選ぶ - タグ検索クリック
export const sendMaterialTagClickEvent = (
  materialTagId: number,
  materialTagName: string,
  pageLink: string,
  user_id: number,
) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'material_tag_click',
    materialTagId,
    materialTagName,
    pageLink,
    user_id,
  });
};

// 素材を選ぶ - アイテム選択クリック
export const sendSelectItemEvent = (items: PurchaseItem[]) => {
  // GA4推奨イベント
  getDataLayer().push({ ecommerce: null });
  getDataLayer().push({
    event: 'select_item',
    ecommerce: {
      items: items,
    },
  });
};

// 商品を閲覧
export const sendViewItemEvent = (items: PurchaseItem[]) => {
  // GA4推奨イベント
  getDataLayer().push({ ecommerce: null });
  getDataLayer().push({
    event: 'view_item',
    ecommerce: {
      items: items,
    },
  });
};

// 商品リストを閲覧
export const sendViewItemListEvent = (items: PurchaseItem[], item_list_name: string) => {
  // GA4推奨イベント
  getDataLayer().push({ ecommerce: null });
  getDataLayer().push({
    event: 'view_item_list',
    ecommerce: {
      currency: 'JPY',
      item_list_name: item_list_name,
      items: items,
    },
  });
};

// チェックアウト開始
export const sendBeginCheckoutEvent = (items: PurchaseItem[], value: number) => {
  // GA4推奨イベント
  getDataLayer().push({ ecommerce: null });
  getDataLayer().push({
    event: 'begin_checkout',
    ecommerce: {
      currency: 'JPY',
      //ドキュメントでは税抜推奨だが、一番最初に実装したpurchaseイベントでは税込金額を使用しているため、整合性をとるためこちらも税込金額を使用する。
      value: value,
      items: items,
    },
  });
};

// 素材を選ぶ - お気に入りクリック
export const sendMaterialFavoriteClickEvent = (
  materialId: number,
  materialName: string,
  isFavorite: boolean,
  user_id: number,
) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'material_favorite_click',
    materialId,
    materialName,
    isFavorite,
    user_id,
  });
};

// アイテムのオプション、個数の決定 - デザインに進むボタンクリック
export const sendDesignStartButtonClickEvent = (
  colorName: string,
  sizeName: string,
  quantity: number,
  user_id: number,
) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'design_start_button_click',
    colorName,
    sizeName,
    quantity,
    user_id,
  });
};

// カートに商品を追加
export const sendAddToCartEvent = (items: PurchaseItem[]) => {
  // GA4推奨イベント
  const value = items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0);
  getDataLayer().push({ ecommerce: null });
  getDataLayer().push({
    event: 'add_to_cart',
    ecommerce: {
      currency: 'JPY',
      value: value,
      items: items,
    },
  });
};

// カートから商品を削除
export const sendRemoveFromCartEvent = (items: PurchaseItem[]) => {
  // GA4推奨イベント
  const value = items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0);
  getDataLayer().push({ ecommerce: null });
  getDataLayer().push({
    event: 'remove_from_cart',
    ecommerce: {
      currency: 'JPY',
      value: value,
      items: items,
    },
  });
};

// カートを閲覧
export const sendViewCartEvent = (items: PurchaseItem[], value: number) => {
  // GA4推奨イベント
  getDataLayer().push({ ecommerce: null });
  getDataLayer().push({
    event: 'view_cart',
    ecommerce: {
      currency: 'JPY',
      value: value,
      items: items,
    },
  });
};

// FAQ - 詳細アコーディオンクリック
export const sendFaqAccordionClickEvent = (faqTitle: string, user_id: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'faq_accordion_click',
    faqTitle,
    user_id,
  });
};

// 購入イベント
export const sendPurchaseGAEvent = (orderId: string, totalAmount: number, items: PurchaseItem[], isGuest = false) => {
  // GA4推奨イベント
  getDataLayer().push({ ecommerce: null });
  getDataLayer().push({
    event: 'purchase',
    ecommerce: {
      transaction_id: orderId,
      value: totalAmount, //税込金額
      tax: 0,
      currency: 'JPY',
      items: items,
    },
    isGuest,
  });
};

// みんなのデザイン - このデザインからグッズをつくる
export const sendClickUserDesignCreateGoodsEvent = (
  userPrintId: string | number,
  workId: number,
  workName: string,
  productId: number,
  productName: string,
  colorName: string,
  sizeName: string,
  materialIds: string,
) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'click_user_design_create_goods',
    userPrintId,
    workId,
    workName,
    productId,
    productName,
    colorName,
    sizeName,
    materialIds,
  });
};

// みんなのデザイン - 前後のデザインを入れ替える
export const sendClickShareDesignNavigationEvent = () => {
  // カスタムイベント
  getDataLayer().push({
    event: 'click_user_design_change',
  });
};

// カート - もう一度デザインする
export const sendClickCartRedesignEvent = (userPrintId: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'click_cart_redesign',
    userPrintId,
  });
};

// 注文履歴 - もう一度購入する
export const sendClickPurchaseHistoryRebuyEvent = (orderId: string, userPrintId: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'click_purchase_history_rebuy',
    orderId,
    userPrintId,
  });
};
// カート画面 - 購入画面へ進むボタンクリック
export const sendGoPurchaseEvent = (user_id: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'go_purchase',
    user_id,
  });
};

// 定型購入 - 「カートに追加」クリック
export const sendClickTemplatePurchaseEvent = (
  productId: number,
  productName: string,
  colorName: string,
  sizeName: string,
) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'click_template_purchase',
    productId,
    productName,
    colorName,
    sizeName,
  });
};

// カート画面 - 作品一覧へ遷移ボタンクリック
export const sendGoWorksFromCartEvent = (user_id: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'go_works_from_cart',
    user_id,
  });
};

// 新規登録/ログイン画面 - 登録せずにゲスト購入するボタンクリック
export const sendClickGuestPurchaseEvent = () => {
  // カスタムイベント
  getDataLayer().push({
    event: 'click_guest_purchase',
  });
};

// ゲスト購入後 - 会員登録完了（コンバージョン）
export const sendGuestConversionCompleteEvent = (user_id: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'guest_conversion_complete',
    user_id,
  });
};
